import { NgModule, Inject, PLATFORM_ID, inject, Injectable } from '@angular/core';
import { TransferState, makeStateKey } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpHandler, HttpInterceptor, provideHttpClient, withInterceptorsFromDi, HttpRequest, HTTP_INTERCEPTORS } from '@angular/common/http';
import { provideNamedApollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { InMemoryCache, ApolloClient } from '@apollo/client/core';
import type { DocumentNode } from 'graphql';
import { ShellDomainProvider } from '../core';

const STATE_KEY = makeStateKey<any>('apollo.state');
// Apollo Client 4 removed InMemoryCache's `addTypename: false` and always adds `__typename` to every query.
// The platform GraphQL endpoint rejects it ("Failed to qualify UserReadableDatas"), so keep documents as written.
class NoTypenameInMemoryCache extends InMemoryCache {
    override transformDocument(document: DocumentNode): DocumentNode {
        return document;
    }
}

const SHARED_CACHE = new NoTypenameInMemoryCache();

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

    intercept(req: HttpRequest<any>, next: HttpHandler) {
        let token: string | null = null;

        if (isPlatformBrowser(PLATFORM_ID)) {
            token = 'eyJhbGciOiJodHRwOi8vd3d3LnczLm9yZy8yMDAxLzA0L3htbGRzaWctbW9yZSNyc2Etc2hhMjU2IiwidHlwIjoiSldUIn0.eyJ0ZW5hbnRfaWQiOiJGQzAwNjI3OC0yRDUxLTQ3MDMtOTUyQi1DMzEwMjY3NDYwMjYiLCJzdWIiOiI4MjQ2Nzk1OC00NzY4LTRlNjQtOTI4ZC1hZTkyYTg4ZWQ2NWUiLCJzaXRlX2lkIjoiNEFERDY4QzUtMkIzRC00NDc4LUFGQzYtMTE1MTBGOTU1QjkxIiwib3JpZ2luIjoic3dpc3NwYXJ0bmVyc3BvcnRhbC5mYWtlZG9tYWluLmNvbSIsInNlc3Npb25faWQiOiJlZTVlNTg3MTk4MTU0NzY0YTNiYThmMzk3OGRmMjY1NyIsInVzZXJfaWQiOiI4MjQ2Nzk1OC00NzY4LTRlNjQtOTI4ZC1hZTkyYTg4ZWQ2NWUiLCJkaXNwbGF5X25hbWUiOiJrYW1ydXp6YW1hbiB6YW1hbiIsInNpdGVfbmFtZSI6IkVjYXAgVGVhbSIsInVzZXJfbmFtZSI6Im1kLmthbXJ1enphbWFuQHNlbGlzZS5jaCIsImVtYWlsIjoibWQua2FtcnV6emFtYW5Ac2VsaXNlLmNoIiwicGhvbmVfbnVtYmVyIjoiKzg4MDE3MjI3NTM0OTciLCJsYW5ndWFnZSI6ImVuLVVTIiwidXNlcl9sb2dnZWRpbiI6IlRydWUiLCJuYW1lIjoiODI0Njc5NTgtNDc2OC00ZTY0LTkyOGQtYWU5MmE4OGVkNjVlIiwicm9sZSI6WyJhZG1pbiIsImFwcHVzZXIiLCJhbm9ueW1vdXMiXSwibmJmIjoxNTQ5MjAwMTc4LCJleHAiOjE1OTEyMDAxNzgsImlzcyI6IkNOPUVudGVycHJpc2UgQ2xvdWQgQXBwbGljYXRpb24gUGxhdGZvcm0iLCJhdWQiOiIqIn0.YwBsw1tRl2FwZyw-0r25g_Jbj7XvZ0FSqvbIRKnDJOTr5fF55n4mltDP0bm7c1SnSYjOsklgsoQ71NLHD1Mq3n4geJb_Vdf--elAgUZYJxQOxQgzNiQWf3DEOPiv_liUN9kcl_bRGoqtiWqZ9Ft4ZHEmTLcOMQim_tISXSc9F3NzXp2HL5nq6HFySpl21EOKoLpV2_KHRKhfVD9ePmWi5nfK9WX1WLJJKyWHYfJHHJoa_GRqEkLsunqmq7P4yADK_SEbtbKGVbhH7JixUjUf0gZjAvODN8SP1jmAPRNW5RWgxvG_JnxT0aF_BLbitCNQw94qgWtSdjg0qrgafegKug';
        }

        if (token) {
            req = req.clone({
                setHeaders: {
                    Authorization: `Bearer ${token}`
                }
            });
        }

        return next.handle(req);
    }
}

@NgModule({
    providers: [
        provideHttpClient(withInterceptorsFromDi()),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthInterceptor,
            multi: true
        },
        provideNamedApollo(provideApolloClients)
    ]
})

export class GraphQLModule {
    constructor(
        private readonly transferState: TransferState,
        @Inject(PLATFORM_ID) private readonly platformId: Object
    ) {
        const isBrowser = isPlatformBrowser(this.platformId);

        if (isBrowser) {
            const state = this.transferState.get<any>(STATE_KEY, null);
            if (state) {
                SHARED_CACHE.restore(state);
            }
        } else {
            this.transferState.onSerialize(STATE_KEY, () => {
                return SHARED_CACHE.extract();
            });
        }
    }
}

export function provideApolloClients(): Record<string, ApolloClient.Options> {
    const httpLink = inject(HttpLink);
    const queryUri = ShellDomainProvider.api.GQLQueryUri;
    const mutationUri = ShellDomainProvider.api.GQLMutationUri;

    // Apollo Client 4 defaults notifyOnNetworkStatusChange to true, which makes valueChanges emit an initial
    // `loading: true` result with no data. Callers using take(1) would take that and unsubscribe, aborting the request.
    const defaultOptions = {
        watchQuery: {
            fetchPolicy: 'network-only',
            errorPolicy: 'ignore',
            notifyOnNetworkStatusChange: false
        },
        query: {
            fetchPolicy: 'network-only',
            errorPolicy: 'all'
        }
    };

    return {
        default: {
            link: httpLink.create({
                uri: queryUri,
                withCredentials: true,
                method: 'POST'
            }),
            cache: SHARED_CACHE,
            defaultOptions,
            queryDeduplication: false
        },
        Mutations: {
            link: httpLink.create({
                uri: mutationUri,
                withCredentials: true,
                method: 'POST'
            }),
            cache: SHARED_CACHE,
            defaultOptions,
            queryDeduplication: false
        },
        QueryCache: {
            link: httpLink.create({
                uri: queryUri,
                withCredentials: true,
                method: 'POST'
            }),
            cache: SHARED_CACHE,
            defaultOptions: { watchQuery: { notifyOnNetworkStatusChange: false } }
        },
        MutationsCache: {
            link: httpLink.create({
                uri: mutationUri,
                withCredentials: true,
                method: 'POST'
            }),
            cache: SHARED_CACHE
        }
    } as Record<string, ApolloClient.Options>;
}